/**
 * Task API client
 *
 * Provides methods for interacting with the Task Manager API.
 */

import { fetchApi, buildQueryString } from './client';
import type {
  Task,
  TaskListResponse,
  TaskViewResponse,
  TaskStatsResponse,
  TaskFilters,
  CreateTaskInput,
  UpdateTaskInput,
} from '@/types';

// Task API base path - relative to API_BASE which is /api
// So this becomes /api/tasks when used with fetchApi
const TASKS_API_BASE = import.meta.env.VITE_TASKS_API_BASE ?? '/tasks';

/**
 * List tasks with optional filters
 */
export async function listTasks(
  filters?: TaskFilters,
  options?: {
    limit?: number;
    offset?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }
): Promise<TaskListResponse> {
  const queryParams = {
    ...filters,
    q: filters?.search,
    limit: options?.limit,
    offset: options?.offset,
    sort_by: options?.sortBy,
    sort_order: options?.sortOrder,
  };

  // Remove 'search' as we use 'q' instead
  delete (queryParams as Record<string, unknown>).search;

  const query = buildQueryString(queryParams as Record<string, string | number | boolean | undefined>);
  return fetchApi<TaskListResponse>(`${TASKS_API_BASE}${query}`);
}

/**
 * Get a single task by ID
 */
export async function getTask(taskId: string): Promise<{ task: Task }> {
  return fetchApi<{ task: Task }>(`${TASKS_API_BASE}/${taskId}`);
}

/**
 * Get tasks due today
 */
export async function getTasksToday(): Promise<TaskViewResponse> {
  return fetchApi<TaskViewResponse>(`${TASKS_API_BASE}/views/today`);
}

/**
 * Get tasks due this week
 */
export async function getTasksWeek(): Promise<TaskViewResponse> {
  return fetchApi<TaskViewResponse>(`${TASKS_API_BASE}/views/week`);
}

/**
 * Get overdue tasks
 */
export async function getTasksOverdue(): Promise<TaskViewResponse> {
  return fetchApi<TaskViewResponse>(`${TASKS_API_BASE}/views/overdue`);
}

/**
 * Response type for focus view (includes focus topics)
 */
export interface FocusViewResponse extends TaskViewResponse {
  focusTopics: Record<string, number>;
}

/**
 * Get focused tasks
 */
export async function getTasksFocus(): Promise<FocusViewResponse> {
  return fetchApi<FocusViewResponse>(`${TASKS_API_BASE}/views/focus`);
}

/**
 * Inbox (unverified tasks) response
 */
export interface InboxViewResponse extends TaskViewResponse {
  count: number;
}

/**
 * Get inbox (unverified) tasks for triage
 */
export async function getTasksInbox(): Promise<InboxViewResponse> {
  return fetchApi<InboxViewResponse>(`${TASKS_API_BASE}/views/inbox`);
}

/**
 * Get task statistics
 */
export async function getTaskStats(): Promise<TaskStatsResponse> {
  return fetchApi<TaskStatsResponse>(`${TASKS_API_BASE}/stats`);
}

/**
 * Create a new task
 */
export async function createTask(input: CreateTaskInput): Promise<{ task: Task }> {
  return fetchApi<{ task: Task }>(TASKS_API_BASE, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/**
 * Update a task
 */
export async function updateTask(
  taskId: string,
  input: UpdateTaskInput
): Promise<{ task: Task }> {
  return fetchApi<{ task: Task }>(`${TASKS_API_BASE}/${taskId}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
}

/**
 * Delete a task (soft delete)
 */
export async function deleteTask(taskId: string): Promise<{ success: boolean }> {
  return fetchApi<{ success: boolean }>(`${TASKS_API_BASE}/${taskId}`, {
    method: 'DELETE',
  });
}

/**
 * Mark a task as complete
 */
export async function completeTask(taskId: string): Promise<{ task: Task }> {
  return fetchApi<{ task: Task }>(`${TASKS_API_BASE}/${taskId}/complete`, {
    method: 'POST',
  });
}

/**
 * Reopen a completed task
 */
export async function reopenTask(taskId: string): Promise<{ task: Task }> {
  return fetchApi<{ task: Task }>(`${TASKS_API_BASE}/${taskId}/reopen`, {
    method: 'POST',
  });
}

/**
 * Mark a task as focused
 */
export async function focusTask(taskId: string): Promise<{ task: Task }> {
  return fetchApi<{ task: Task }>(`${TASKS_API_BASE}/${taskId}/focus`, {
    method: 'POST',
  });
}

/**
 * Remove focus from a task
 */
export async function unfocusTask(taskId: string): Promise<{ task: Task }> {
  return fetchApi<{ task: Task }>(`${TASKS_API_BASE}/${taskId}/unfocus`, {
    method: 'POST',
  });
}

/**
 * Toggle focus status on a task
 */
export async function toggleFocus(taskId: string, isFocus: boolean): Promise<{ task: Task }> {
  return isFocus ? focusTask(taskId) : unfocusTask(taskId);
}

/**
 * Reschedule a task (update due date)
 */
export async function rescheduleTask(
  taskId: string,
  dueAt: string | null
): Promise<{ task: Task }> {
  return updateTask(taskId, { dueAt });
}

/**
 * Set task priority
 */
export async function setTaskPriority(
  taskId: string,
  priority: 'high' | 'medium' | 'low'
): Promise<{ task: Task }> {
  return updateTask(taskId, { priority });
}

/**
 * Dismiss a task (soft delete - won't reappear after sync)
 * Use when the task is not relevant to you (e.g., someone else's task)
 */
export async function dismissTask(taskId: string): Promise<{ success: boolean }> {
  // First, deactivate the sync mapping so it won't come back from Asana
  try {
    const syncApiBase = import.meta.env.VITE_ASANA_SYNC_API_BASE ?? 
      'https://us-west1-assistant-481119.cloudfunctions.net';
    await fetch(`${syncApiBase}/asana-deactivate-mapping`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ lifeguide_task_id: taskId }),
    });
  } catch (e) {
    // Log but don't fail - the task will still be deleted
    console.warn('Failed to deactivate sync mapping:', e);
  }
  
  // Then soft delete the task
  return deleteTask(taskId);
}

/**
 * Convert task to a reference/notable (not actionable)
 * Use when it's relevant information but not a task
 */
export async function convertToReference(taskId: string): Promise<{ task: Task }> {
  return updateTask(taskId, { 
    status: 'cancelled',
    context: 'Converted to reference - not an actionable task',
    tags: ['#reference'],
  });
}

// =============================================================================
// TASK-VALUE EDGES (Values a task supports)
// =============================================================================

/**
 * Task value association
 */
export interface TaskValue {
  valueId: string;
  strength?: number;
}

/**
 * Response for task values query
 */
export interface TaskValuesResponse {
  taskId: string;
  values: TaskValue[];
}

/**
 * Response for setting task values
 */
export interface SetTaskValuesResponse {
  taskId: string;
  valueCount: number;
  edges: Array<{
    id: string;
    edgeType: string;
    fromId: string;
    toId: string;
    strength?: number;
    source: string;
  }>;
}

/**
 * Get values that a task supports (serves)
 */
export async function getTaskValues(taskId: string): Promise<TaskValuesResponse> {
  return fetchApi<TaskValuesResponse>(`${TASKS_API_BASE}/${taskId}/values`);
}

/**
 * Set the values that a task supports (replaces existing associations)
 */
export async function setTaskValues(
  taskId: string,
  valueIds: string[],
  source: string = 'manual'
): Promise<SetTaskValuesResponse> {
  return fetchApi<SetTaskValuesResponse>(`${TASKS_API_BASE}/${taskId}/values`, {
    method: 'PUT',
    body: JSON.stringify({ valueIds, source }),
  });
}

// =============================================================================
// TASK-GOAL EDGES (Goals a task supports/advances)
// =============================================================================

/**
 * Task goal association
 */
export interface TaskGoal {
  goalId: string;
  strength?: number;
}

/**
 * Response for task goals query
 */
export interface TaskGoalsResponse {
  taskId: string;
  goals: TaskGoal[];
}

/**
 * Response for setting task goals
 */
export interface SetTaskGoalsResponse {
  taskId: string;
  goalCount: number;
  edges: Array<{
    id: string;
    edgeType: string;
    fromId: string;
    toId: string;
    strength?: number;
    source: string;
  }>;
}

/**
 * Get goals that a task supports/advances
 */
export async function getTaskGoals(taskId: string): Promise<TaskGoalsResponse> {
  return fetchApi<TaskGoalsResponse>(`${TASKS_API_BASE}/${taskId}/goals`);
}

/**
 * Set the goals that a task supports/advances (replaces existing associations)
 */
export async function setTaskGoals(
  taskId: string,
  goalIds: string[],
  source: string = 'manual'
): Promise<SetTaskGoalsResponse> {
  return fetchApi<SetTaskGoalsResponse>(`${TASKS_API_BASE}/${taskId}/goals`, {
    method: 'PUT',
    body: JSON.stringify({ goalIds, source }),
  });
}

// =============================================================================
// TASK VERIFICATION (Inbox Triage)
// =============================================================================

/**
 * Bulk verification response
 */
export interface BulkActionResponse {
  verified?: number;
  dismissed?: number;
  failed: number;
}

/**
 * Verify a task (move from inbox to active tasks)
 */
export async function verifyTask(taskId: string): Promise<{ task: Task }> {
  return fetchApi<{ task: Task }>(`${TASKS_API_BASE}/${taskId}/verify`, {
    method: 'POST',
  });
}

/**
 * Bulk verify multiple tasks
 */
export async function bulkVerifyTasks(taskIds: string[]): Promise<BulkActionResponse> {
  return fetchApi<BulkActionResponse>(`${TASKS_API_BASE}/bulk/verify`, {
    method: 'POST',
    body: JSON.stringify({ taskIds }),
  });
}

/**
 * Bulk dismiss multiple tasks
 */
export async function bulkDismissTasks(taskIds: string[]): Promise<BulkActionResponse> {
  return fetchApi<BulkActionResponse>(`${TASKS_API_BASE}/bulk/dismiss`, {
    method: 'POST',
    body: JSON.stringify({ taskIds }),
  });
}